import { Ref, useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { SurveyQuestion } from 'types/survey';
import { Answer } from 'types/surveyResponse';
import { TextInput } from 'components/ui/TextInput';
import { getNumericValue } from '../helpers';

interface IProps {
  question: SurveyQuestion;
  answer: Answer;
  onChange: (answer: Answer) => void;
}

export const NumericQuestion = ({ question, answer, onChange }: IProps) => {
  let defaultValue = question.minValue ?? 0;
  if (typeof answer.value === 'number') {
    defaultValue = answer.value;
  }

  const [value, setValue] = useState(defaultValue.toString());
  const [valid, setValid] = useState(true);
  const onChangeDebounced = useDebouncedCallback((value) => onChange({ ...answer, value }), 1500, { maxWait: 10000 });
  const inputRef = useRef<HTMLInputElement>();

  const onInput = (value: string) => {
    value = value.replace(/,/g, '.');
    setValue(value);
    const numValue = getNumericValue(value);

    if (
      isNaN(numValue) ||
      (question.minValue && numValue < question.minValue) ||
      (question.maxValue && numValue > question.maxValue)
    ) {
      setValid(false);
    } else {
      setValid(true);
      onChangeDebounced(numValue);
    }
  };

  const onAdd = (add: number) => {
    let numValue = parseFloat(value);
    if (
      isNaN(numValue) ||
      (question.minValue && numValue < question.minValue) ||
      (question.maxValue && numValue > question.maxValue)
    ) {
      if (typeof answer.value === 'number') {
        numValue = answer.value;
      } else {
        numValue = defaultValue;
      }
    }

    numValue += add;
    if (
      (typeof question.minValue !== 'number' || numValue >= question.minValue) &&
      (typeof question.maxValue !== 'number' || numValue <= question.maxValue)
    ) {
      setValid(true);
      setValue(numValue.toString());
      onChangeDebounced(numValue);
    }
  };

  useEffect(() => {
    const numValueString = getNumericValue(value).toString();
    inputRef.current!.size = numValueString.length > 0 ? numValueString.length : 1;
  }, [value]);

  // make sure to do one last update on component unmount
  useEffect(() => {
    return () => {
      onChangeDebounced.flush();
    };
  }, []);

  return (
    <div>
      <TextInput
        name={'question-' + question.questionId}
        text={value}
        onChange={(e) => onInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp') {
            onAdd(1);
            e.preventDefault();
          }
          if (e.key === 'ArrowDown') {
            onAdd(-1);
            e.preventDefault();
          }
        }}
        state={valid ? undefined : 'error'}
        inputRef={inputRef as Ref<HTMLInputElement | HTMLTextAreaElement>}
      />
    </div>
  );
};
